import * as THREE from 'three';
import { CAR, MAP, POLICE_HAZARDS } from '../utils/constants';

export type PoliceHazardKind = 'roadblock' | 'spikes';

export type PoliceHazardHit = {
  kind: PoliceHazardKind;
  impulse: number;
  x: number;
  z: number;
  nitroPierce?: boolean;
};

export type PoliceHazardWarning = {
  kind: PoliceHazardKind;
};

type PoliceHazard = {
  kind: PoliceHazardKind;
  group: THREE.Group;
  x: number;
  z: number;
  heading: number;
  halfLength: number;
  halfDepth: number;
  warned: boolean;
  hitCooldownUntil: number;
};

export type PoliceHazardsHandle = {
  group: THREE.Group;
  reset(): void;
  update(input: {
    dt: number;
    frame: number;
    playerX: number;
    playerZ: number;
    playerHeading: number;
    roadblocksEnabled: boolean;
    spikeStripsEnabled: boolean;
  }): PoliceHazardWarning | null;
  resolvePlayer(input: {
    frame: number;
    x: number;
    z: number;
    vx: number;
    vz: number;
    heading: number;
    nitroTank: number;
    nitroBoosting: boolean;
  }): {
    x: number;
    z: number;
    vx: number;
    vz: number;
    nitroTank: number;
    hit: PoliceHazardHit | null;
  };
};

const roadblockMat = new THREE.MeshStandardMaterial({
  color: 0x111318,
  roughness: 0.58,
  metalness: 0.22,
  emissive: 0x2a0505,
  emissiveIntensity: 0.16,
});

const hazardStripeMat = new THREE.MeshStandardMaterial({
  color: 0xffc72c,
  roughness: 0.48,
  metalness: 0.08,
  emissive: 0x4a3000,
  emissiveIntensity: 0.16,
});

const spikeMat = new THREE.MeshStandardMaterial({
  color: 0xcfd7df,
  roughness: 0.36,
  metalness: 0.75,
  emissive: 0x1e2b35,
  emissiveIntensity: 0.12,
});

function roadCenters(): number[] {
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH, SIZE } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const out: number[] = [];
  for (let i = 0; i <= GRID; i++) {
    out.push(-HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2);
  }
  return out;
}

const centers = roadCenters();

function nearestRoadCenter(v: number): number {
  let best = centers[0] ?? 0;
  let bestD = Infinity;
  for (const c of centers) {
    const d = Math.abs(v - c);
    if (d < bestD) {
      best = c;
      bestD = d;
    }
  }
  return best;
}

function createRoadblockMesh(): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(POLICE_HAZARDS.ROADBLOCK_LENGTH, 1.35, POLICE_HAZARDS.ROADBLOCK_DEPTH),
    roadblockMat,
  );
  base.position.y = 0.68;
  base.castShadow = true;
  g.add(base);

  for (let i = -2; i <= 2; i++) {
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(1.15, 1.42, POLICE_HAZARDS.ROADBLOCK_DEPTH + 0.04),
      hazardStripeMat,
    );
    stripe.position.set(i * 2.2, 0.72, 0.03);
    stripe.rotation.z = i % 2 === 0 ? 0.28 : -0.28;
    stripe.castShadow = true;
    g.add(stripe);
  }
  return g;
}

function createSpikeMesh(): THREE.Group {
  const g = new THREE.Group();
  const rail = new THREE.Mesh(
    new THREE.BoxGeometry(POLICE_HAZARDS.SPIKE_LENGTH, 0.12, POLICE_HAZARDS.SPIKE_DEPTH),
    roadblockMat,
  );
  rail.position.y = 0.08;
  rail.receiveShadow = true;
  g.add(rail);

  const spikeCount = 9;
  for (let i = 0; i < spikeCount; i++) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.75, 4), spikeMat);
    const t = i / (spikeCount - 1);
    spike.position.set((t - 0.5) * POLICE_HAZARDS.SPIKE_LENGTH * 0.86, 0.45, 0);
    spike.rotation.x = Math.PI;
    spike.rotation.y = Math.PI * 0.25;
    spike.castShadow = true;
    g.add(spike);
  }
  return g;
}

function toLocal(h: PoliceHazard, x: number, z: number): { lx: number; lz: number } {
  const dx = x - h.x;
  const dz = z - h.z;
  const c = Math.cos(h.heading);
  const s = Math.sin(h.heading);
  return {
    lx: dx * c - dz * s,
    lz: dx * s + dz * c,
  };
}

export function createPoliceHazards(): PoliceHazardsHandle {
  const group = new THREE.Group();
  group.name = 'police-hazards';
  const hazards: PoliceHazard[] = [];
  let spawnAcc = 0;
  let roadblockCooldown = 0;
  let spikeCooldown = 0;

  function spawn(kind: PoliceHazardKind, playerX: number, playerZ: number, heading: number): void {
    if (hazards.length >= POLICE_HAZARDS.MAX_ACTIVE) return;
    const forwardX = Math.sin(heading);
    const forwardZ = Math.cos(heading);
    const dist =
      POLICE_HAZARDS.MIN_SPAWN_DIST +
      Math.random() * (POLICE_HAZARDS.MAX_SPAWN_DIST - POLICE_HAZARDS.MIN_SPAWN_DIST);
    let x = playerX + forwardX * dist;
    let z = playerZ + forwardZ * dist;
    const snapX = nearestRoadCenter(x);
    const snapZ = nearestRoadCenter(z);
    const verticalRoad = Math.abs(x - snapX) < Math.abs(z - snapZ);
    let hazardHeading = 0;

    if (verticalRoad) {
      x = snapX;
      hazardHeading = 0;
    } else {
      z = snapZ;
      hazardHeading = Math.PI * 0.5;
    }

    const lim = MAP.HALF - 38;
    x = THREE.MathUtils.clamp(x, -lim, lim);
    z = THREE.MathUtils.clamp(z, -lim, lim);

    const mesh = kind === 'roadblock' ? createRoadblockMesh() : createSpikeMesh();
    mesh.position.set(x, 0.04, z);
    mesh.rotation.y = hazardHeading;
    group.add(mesh);

    hazards.push({
      kind,
      group: mesh,
      x,
      z,
      heading: hazardHeading,
      halfLength:
        (kind === 'roadblock' ? POLICE_HAZARDS.ROADBLOCK_LENGTH : POLICE_HAZARDS.SPIKE_LENGTH) *
        0.5,
      halfDepth:
        (kind === 'roadblock' ? POLICE_HAZARDS.ROADBLOCK_DEPTH : POLICE_HAZARDS.SPIKE_DEPTH) *
        0.5,
      warned: false,
      hitCooldownUntil: -1,
    });
  }

  function removeAt(index: number): void {
    const h = hazards[index];
    if (!h) return;
    group.remove(h.group);
    h.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry.dispose();
      }
    });
    hazards.splice(index, 1);
  }

  return {
    group,
    reset(): void {
      for (let i = hazards.length - 1; i >= 0; i--) removeAt(i);
      spawnAcc = 0;
      roadblockCooldown = 0;
      spikeCooldown = 0;
    },
    update(input): PoliceHazardWarning | null {
      spawnAcc += input.dt;
      roadblockCooldown = Math.max(0, roadblockCooldown - input.dt);
      spikeCooldown = Math.max(0, spikeCooldown - input.dt);

      const forwardX = Math.sin(input.playerHeading);
      const forwardZ = Math.cos(input.playerHeading);
      let warning: PoliceHazard | null = null;

      for (let i = hazards.length - 1; i >= 0; i--) {
        const h = hazards[i]!;
        const dx = h.x - input.playerX;
        const dz = h.z - input.playerZ;
        const dist = Math.hypot(dx, dz);
        if (dist > POLICE_HAZARDS.DESPAWN_DIST) {
          removeAt(i);
          continue;
        }
        const ahead = dist > 1e-4 ? (dx / dist) * forwardX + (dz / dist) * forwardZ : 0;
        if (
          !h.warned &&
          dist < POLICE_HAZARDS.WARNING_DIST &&
          ahead > POLICE_HAZARDS.WARNING_DOT_MIN
        ) {
          h.warned = true;
          warning = h;
        }
      }

      if (spawnAcc >= POLICE_HAZARDS.SPAWN_CHECK_S) {
        spawnAcc = 0;
        if (input.roadblocksEnabled && roadblockCooldown <= 0) {
          spawn('roadblock', input.playerX, input.playerZ, input.playerHeading);
          roadblockCooldown = POLICE_HAZARDS.ROADBLOCK_COOLDOWN_S;
        } else if (input.spikeStripsEnabled && spikeCooldown <= 0) {
          spawn('spikes', input.playerX, input.playerZ, input.playerHeading);
          spikeCooldown = POLICE_HAZARDS.SPIKE_COOLDOWN_S;
        }
      }

      return warning;
    },
    resolvePlayer(input) {
      let out = { ...input };
      let hit: PoliceHazardHit | null = null;
      for (const h of hazards) {
        if (h.hitCooldownUntil > input.frame) continue;
        const p = toLocal(h, out.x, out.z);
        if (
          Math.abs(p.lx) > h.halfLength + CAR.COLLISION_RADIUS ||
          Math.abs(p.lz) > h.halfDepth + CAR.COLLISION_RADIUS
        ) {
          continue;
        }

        h.hitCooldownUntil = input.frame + POLICE_HAZARDS.HIT_COOLDOWN_FRAMES;
        if (h.kind === 'spikes') {
          out.vx *= POLICE_HAZARDS.SPIKE_SPEED_LOSS;
          out.vz *= POLICE_HAZARDS.SPIKE_SPEED_LOSS;
          out.nitroTank = Math.max(0, out.nitroTank - POLICE_HAZARDS.SPIKE_NITRO_DRAIN);
          hit = { kind: h.kind, impulse: 4.6, x: h.x, z: h.z };
        } else if (input.nitroBoosting) {
          out.vx *= POLICE_HAZARDS.ROADBLOCK_NITRO_PIERCE_SPEED_KEEP;
          out.vz *= POLICE_HAZARDS.ROADBLOCK_NITRO_PIERCE_SPEED_KEEP;
          removeAt(hazards.indexOf(h));
          hit = { kind: h.kind, impulse: 5.8, x: h.x, z: h.z, nitroPierce: true };
        } else {
          out.vx *= -POLICE_HAZARDS.ROADBLOCK_SPEED_LOSS;
          out.vz *= -POLICE_HAZARDS.ROADBLOCK_SPEED_LOSS;
          out.x -= Math.sin(out.heading) * 2.1;
          out.z -= Math.cos(out.heading) * 2.1;
          hit = { kind: h.kind, impulse: 7.2, x: h.x, z: h.z };
        }
        break;
      }
      return { ...out, hit };
    },
  };
}
